import apiClient from './api';

/**
 * Service pour gérer les détails d'un représentant légal
 */
class LegalRepresentativeDetailService {
  
  /**
   * Récupérer les détails complets d'un représentant légal
   * @param {number} representativeId - ID du représentant légal
   * @returns {Promise<Object>} Détails du représentant légal
   */
  async getRepresentativeDetail(representativeId) {
    try {
      const response = await apiClient.get(
        `/api/LegalRepresentatives/${representativeId}`
      );
      
      return {
        success: true,
        data: response.data.data,
        error: null
      };
    } catch (error) {
      // console.error('Error fetching representative detail:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || error.message || 'Erreur lors du chargement des détails'
      };
    }
  }

  /**
   * Formater le type de mandat pour l'affichage
   * @param {string} mandateType - Type de mandat technique
   * @returns {string} Type de mandat formaté
   */
  formatMandateType(mandateType) {
    const mandateTypes = {
      'LEGAL_GUARDIAN': 'Tuteur Légal',
      'TUTORSHIP': 'Tutelle',
      'CURATORSHIP': 'Curatelle',
      'POWER_OF_ATTORNEY': 'Procuration',
      'MANDATARY': 'Mandataire',
      'PARENT': 'Parent',
      'OTHER': 'Autre'
    };
    
    return mandateTypes[mandateType] || mandateType;
  }

  /**
   * Formater le type de relation pour l'affichage
   * @param {string} relationshipType - Type de relation technique
   * @returns {string} Type de relation formaté
   */
  formatRelationshipType(relationshipType) {
    const relationshipTypes = {
      'PARENT': 'Parent',
      'CHILD': 'Enfant',
      'SPOUSE': 'Conjoint(e)',
      'LEGAL_GUARDIAN': 'Tuteur Légal',
      'ATTORNEY': 'Mandataire',
      'OTHER': 'Autre'
    };
    
    return relationshipTypes[relationshipType] || relationshipType;
  }

  /**
   * Obtenir la classe CSS pour le badge de statut
   * @param {string} status - Statut du mandat
   * @returns {string} Classes CSS pour le badge
   */
  getStatusBadgeClass(status) {
    const statusClasses = {
      'ACTIVE': 'bg-success bg-opacity-10 text-success border border-success border-opacity-25',
      'INACTIVE': 'bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25',
      'PENDING': 'bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25',
      'EXPIRED': 'bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25',
      'REVOKED': 'bg-dark bg-opacity-10 text-dark border border-dark border-opacity-25'
    };
    
    return statusClasses[status] || 'bg-secondary bg-opacity-10 text-secondary';
  }

  /**
   * Formater une date pour l'affichage
   * @param {string} dateString - Date à formater
   * @returns {string} Date formatée
   */
  formatDate(dateString) {
    if (!dateString || dateString === '0001-01-01T00:00:00') return 'Non défini';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      // console.error('Error formatting date:', error);
      return 'Date invalide';
    }
  }

  /**
   * Obtenir l'icône selon le type de mandat
   * @param {string} mandateType - Type de mandat
   * @returns {string} Classe CSS de l'icône
   */
  getMandateIcon(mandateType) {
    const iconMap = {
      'LEGAL_GUARDIAN': 'fas fa-user-shield',
      'TUTORSHIP': 'fas fa-hands-helping',
      'CURATORSHIP': 'fas fa-user-check',
      'POWER_OF_ATTORNEY': 'fas fa-file-signature',
      'MANDATARY': 'fas fa-user-tie',
      'PARENT': 'fas fa-user-friends',
      'OTHER': 'fas fa-user'
    };
    
    return iconMap[mandateType] || 'fas fa-user';
  }

  /**
   * Obtenir la classe de badge selon le type de mandat
   * @param {string} mandateType - Type de mandat
   * @returns {string} Classes CSS pour le badge
   */
  getMandateBadgeClass(mandateType) {
    const badgeMap = {
      'LEGAL_GUARDIAN': 'bg-white bg-opacity-10 text-primary border border-primary border-opacity-25',
      'TUTORSHIP': 'bg-info bg-opacity-10 text-info border border-info border-opacity-25',
      'CURATORSHIP': 'bg-success bg-opacity-10 text-success border border-success border-opacity-25',
      'POWER_OF_ATTORNEY': 'bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25',
      'MANDATARY': 'bg-dark bg-opacity-10 text-dark border border-dark border-opacity-25',
      'PARENT': 'bg-purple bg-opacity-10 text-purple border border-purple border-opacity-25',
      'OTHER': 'bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25'
    };
    
    return badgeMap[mandateType] || 'bg-secondary bg-opacity-10 text-secondary';
  }
}

// Export en tant que singleton
export default new LegalRepresentativeDetailService();